import { atom, PrimitiveAtom } from 'jotai'

import { currentEnv } from '@/env/vars'
import { queryClient } from '@/QueryClient'
import { IAgentVisuals, IMessageVisuals, ISystemAgentVisuals } from '@/types/gigiChat/Chat'
import { IStructureAnswer } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { IChatStatus } from '@/types/gigiChat/LiveAgent'
import { GET, POST } from '@/utils/fetch'

import { store } from '..'
import { chatIdAtom, chatSettingsAtom, partialMessagesAtom, postChatSettings, updateConversationSettings } from './ChatAtom'

export const chatVisualsAtom: PrimitiveAtom<IMessageVisuals | null> = atom<IMessageVisuals | null>(null)
export const agentVisualsAtom: PrimitiveAtom<IAgentVisuals[]> = atom<IAgentVisuals[]>([])
export const systemAgentVisualsAtom: PrimitiveAtom<ISystemAgentVisuals[]> = atom<ISystemAgentVisuals[]>([])
export const chatStatusAtom: PrimitiveAtom<IChatStatus | null> = atom<IChatStatus | null>(null)

export const getChatVisuals = async (chatId: string) => {
    const res = await queryClient.fetchQuery({
        queryKey: [`visuals/${chatId}`],
        queryFn: () => GET<IMessageVisuals>(`${currentEnv.coreChatUrl}/chats/${chatId}/visuals`),
    })

    return res || null
}

export const getTopLabel = (chatStatus: IChatStatus | null) => {
    const chatSettings = store.get(chatSettingsAtom)
    const chatVisuals = store.get(chatVisualsAtom)

    const prompt = chatVisuals?.nextPrompt
    const chatId = store.get(chatIdAtom)

    const asyncProcess = chatStatus?.asyncProcesses.find((process) => process.chatId === chatId)
    if (chatStatus && chatStatus.asyncProcesses.length && asyncProcess) {
        if (asyncProcess && asyncProcess.data.settings.processingModeAgent?.label) {
            return asyncProcess.data.settings.processingModeAgent.label
        }
    } else if (chatVisuals && prompt) {
        if (prompt && prompt.processingModeInfo && prompt.processingModeInfo.label) {
            return prompt.processingModeInfo.label
        }
    } else if (chatSettings && chatSettings.processingModeAgent && chatSettings.processingModeAgent.label) {
        return chatSettings.processingModeAgent.label
    }

    return null
}

export const getStatus = (promptId: number | null) => {
    const chatStatus = store.get(chatStatusAtom)
    const chatVisuals = store.get(chatVisualsAtom)
    const prompt = chatVisuals?.existingPrompts
    const chatId = store.get(chatIdAtom)
    if (chatStatus && chatStatus.asyncProcesses.length) {
        const asyncProcess = chatStatus.asyncProcesses.find((process) => process.chatId === chatId)
        if (asyncProcess && asyncProcess.data.status) {
            return asyncProcess.data.status
        }
    } else if (chatVisuals && prompt && promptId) {
        const matchingPrompt = prompt.find((p) => p.userPromptId === promptId)
        if (matchingPrompt && matchingPrompt.processingModeInfo && matchingPrompt.processingModeInfo.status) {
            return matchingPrompt.processingModeInfo.status
        }
    }

    return null
}

export const getStatusLabel = (promptId: number | null) => {
    const chatStatus = store.get(chatStatusAtom)
    const chatVisuals = store.get(chatVisualsAtom)
    const prompt = chatVisuals?.existingPrompts
    const chatId = store.get(chatIdAtom)
    if (chatStatus && chatStatus.asyncProcesses.length) {
        const asyncProcess = chatStatus.asyncProcesses.find((process) => process.chatId === chatId)
        if (asyncProcess && asyncProcess.data.status) {
            return asyncProcess.data.statusMessage
        }
    } else if (chatVisuals && prompt && promptId) {
        const matchingPrompt = prompt.find((p) => p.userPromptId === promptId)
        if (matchingPrompt && matchingPrompt.processingModeInfo && matchingPrompt.processingModeInfo.status) {
            return matchingPrompt.processingModeInfo.statusMessage
        }
    }

    return null
}

export const shouldDisplayLabel = (settings: IConversationSettings | null) => {
    if (settings && settings.processingModeAgent && settings.processingModeAgent.name) {
        return ['liveagent'].includes(settings.processingModeAgent.name.toLowerCase())
    }

    return false
}

const updateWithLastMessage = (data: IChatStatus) => {
    const chatId = store.get(chatIdAtom)
    const currentMessages = store.get(partialMessagesAtom)

    if (data && data.asyncProcesses && data.asyncProcesses.some((process) => process.chatId === chatId)) {
        const updatedMessages = currentMessages.map((message) => {
            const matchingPrompt = data.asyncProcesses.find((process) => process.data.userPromptId === message.userPromptId)
            if (
                matchingPrompt &&
                ((matchingPrompt.data.answer && matchingPrompt.data.answer !== message.answer) ||
                    (message.structuredAnswer?.callToAction && !matchingPrompt.data.structuredAnswer.callToAction) ||
                    message.citations?.length !== matchingPrompt.data.citations.length)
            ) {
                return {
                    ...message,
                    answer: matchingPrompt.data.answer,
                    citations: matchingPrompt.data.citations,
                    structuredAnswer: matchingPrompt.data.structuredAnswer as unknown as IStructureAnswer,
                }
            }
            return message
        })

        store.set(partialMessagesAtom, updatedMessages)
    }
}

export const getChatStatus = async () => {
    const res = await queryClient.fetchQuery({
        queryKey: ['status'],
        queryFn: () => GET<IChatStatus>(`${currentEnv.coreChatUrl}/chats/status`),
    })

    if (res) {
        updateWithLastMessage(res)
        // use chatStatus as data for development
        store.set(chatStatusAtom, res)
    }
}

export const endPromptConversation = () => {
    const newSettings = { ...store.get(chatSettingsAtom) } as IConversationSettings
    if (newSettings.processingStrategyWriting) {
        const chatId = store.get(chatIdAtom)
        // clear updated
        newSettings.processingStrategyWriting.mode = 'normal'

        // clear new props
        delete newSettings.processingModePromptLibrary
        store.set(chatSettingsAtom, newSettings)
        void postChatSettings(chatId, newSettings, true).then(() => {
            void getChatVisuals(chatId).then((visuals) => {
                store.set(chatVisualsAtom, visuals)
            })
        })
    }
}

export const disconnectAgent = async (mode: string): Promise<void> => {
    const chatId = store.get(chatIdAtom)
    const response = await POST(`${currentEnv.coreChatUrl}/chats/${chatId}/${mode}/disconnect`, {})
    const res = response ? ((await response.json()) as IConversationSettings) : undefined
    if (res) {
        updateConversationSettings(chatId, res)
        store.set(chatSettingsAtom, res)
        void getChatVisuals(chatId).then((visuals) => {
            store.set(chatVisualsAtom, visuals)
        })
    }
}

export const getSystemAgentVisuals = async () => {
    const res = await queryClient.fetchQuery({
        queryKey: ['visuals'],
        queryFn: () => GET<{ data: ISystemAgentVisuals[] }>(`${currentEnv.coreChatUrl}/system/agent/visuals`),
    })

    if (res && res.data) {
        store.set(systemAgentVisualsAtom, res.data)
    }

    return []
}

export const getVisualAgentInfo = (agentName: string | undefined) => {
    if (!agentName) return null
    const systemAgentVisuals = store.get(systemAgentVisualsAtom)
    const agent = systemAgentVisuals.find((item: ISystemAgentVisuals) => item.agentName === agentName)
    return agent || null
}

export const mapAgentColors = (color: string | undefined) => {
    if (!color) {
        return 'var(--chakra-colors-black)'
    }

    switch (color.toLowerCase()) {
        case 'black':
            return 'var(--chakra-colors-black)'
        case 'green':
            return 'var(--chakra-colors-green-500)'
        case 'yellow':
            return 'var(--chakra-colors-yellow-100)'
        case 'red':
            return 'var(--chakra-colors-status-error)'
        case 'orange':
            return 'var(--chakra-colors-accent-500)'
        case 'blue':
            return 'var(--chakra-colors-blue-200)'
        default:
            return 'var(--chakra-colors-black)'
    }
}

//get chat status
;(() => {
    if (currentEnv.features.isEnabled('gigiCore')) {
        setInterval(() => {
            void getChatStatus()
        }, 3 * 1000)
    }
})()
